// "Report to compliance" intake — TanStack Query hooks for the loan
// header report modal + the Compliance tab's Reported issues section.
// Triage mutations also invalidate the cockpit work queue so a triaged
// row re-tints (or leaves the queue) without a refresh.

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { intakeReportsApi } from "@/api/compliance/client";
import type {
  ComplianceIntakeReportDto,
  CreateComplianceIntakeReportRequest,
  EscalateComplianceIntakeReportRequest,
  ResolveComplianceIntakeReportRequest,
  TriageComplianceIntakeReportRequest,
} from "@/api/compliance/intakeReportTypes";
import { workQueueKeys } from "./useWorkQueues";

export const intakeReportKeys = {
  forLoan: (loanId: string) =>
    ["compliance", "intake-reports", loanId] as const,
  detail: (id: string) => ["compliance", "intake-report", id] as const,
};

export function useIntakeReports(loanId: string | undefined) {
  return useQuery<ComplianceIntakeReportDto[]>({
    queryKey: intakeReportKeys.forLoan(loanId ?? ""),
    queryFn: () => intakeReportsApi.listForLoan(loanId!),
    enabled: !!loanId,
  });
}

export function useIntakeReport(id: string | undefined) {
  return useQuery<ComplianceIntakeReportDto>({
    queryKey: intakeReportKeys.detail(id ?? ""),
    queryFn: () => intakeReportsApi.get(id!),
    enabled: !!id,
  });
}

function invalidate(
  qc: ReturnType<typeof useQueryClient>,
  loanId: string,
  id?: string,
) {
  qc.invalidateQueries({ queryKey: intakeReportKeys.forLoan(loanId) });
  if (id) qc.invalidateQueries({ queryKey: intakeReportKeys.detail(id) });
  qc.invalidateQueries({ queryKey: workQueueKeys.compliance });
}

export function useCreateIntakeReport(loanId: string) {
  const qc = useQueryClient();
  return useMutation<
    ComplianceIntakeReportDto,
    unknown,
    CreateComplianceIntakeReportRequest
  >({
    mutationFn: (body) => intakeReportsApi.create(loanId, body),
    onSuccess: () => invalidate(qc, loanId),
  });
}

export function useAcknowledgeIntakeReport(loanId: string, id: string) {
  const qc = useQueryClient();
  return useMutation<
    ComplianceIntakeReportDto,
    unknown,
    TriageComplianceIntakeReportRequest
  >({
    mutationFn: (body) => intakeReportsApi.acknowledge(id, body),
    onSuccess: () => invalidate(qc, loanId, id),
  });
}

export function useInvestigateIntakeReport(loanId: string, id: string) {
  const qc = useQueryClient();
  return useMutation<
    ComplianceIntakeReportDto,
    unknown,
    TriageComplianceIntakeReportRequest
  >({
    mutationFn: (body) => intakeReportsApi.investigate(id, body),
    onSuccess: () => invalidate(qc, loanId, id),
  });
}

export function useResolveIntakeReport(loanId: string, id: string) {
  const qc = useQueryClient();
  return useMutation<
    ComplianceIntakeReportDto,
    unknown,
    ResolveComplianceIntakeReportRequest
  >({
    mutationFn: (body) => intakeReportsApi.resolve(id, body),
    onSuccess: () => invalidate(qc, loanId, id),
  });
}

export function useEscalateIntakeReport(loanId: string, id: string) {
  const qc = useQueryClient();
  return useMutation<
    ComplianceIntakeReportDto,
    unknown,
    EscalateComplianceIntakeReportRequest
  >({
    mutationFn: (body) => intakeReportsApi.escalate(id, body),
    onSuccess: () => {
      invalidate(qc, loanId, id);
      // Escalation opened a DefaultCase — refresh the loan's summary,
      // health, and case list (the Defaults & Workout convention).
      qc.invalidateQueries({
        queryKey: ["portfolio-admin", "loan", loanId],
      });
      qc.invalidateQueries({ queryKey: workQueueKeys.defaults });
    },
  });
}
